import { useEffect, useRef, useState } from "react";
import { ACCENT_IDS, ACCENTS, type AccentId } from "./accents";
import { applyCrmTheme, playCrmThemeFade, readCrmAccent, readCrmTheme, type CrmTheme } from "./theme";

export function ThemeMenu() {
  const [theme, setTheme] = useState<CrmTheme>(readCrmTheme);
  const [accent, setAccent] = useState<AccentId>(readCrmAccent);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const current = ACCENTS[accent];
  const darkish = theme !== "light";

  function pickTheme(next: CrmTheme) {
    setTheme(next);
    applyCrmTheme(next, accent);
    playCrmThemeFade();
  }

  useEffect(() => {
    applyCrmTheme(theme, accent);
  }, [theme, accent]);

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        className={darkish ? "grid h-9 w-9 place-items-center rounded-lg text-slate-200 hover:bg-white/10" : "grid h-9 w-9 place-items-center rounded-lg text-slate-800 hover:bg-slate-100"}
        style={{ color: darkish ? "#e2e8f0" : "#1e293b" }}
        aria-expanded={open}
        aria-label={theme === "plus" ? "Thème — Aurélia+" : `Thème — ${theme === "dark" ? "sombre" : "clair"}, ${current.label}`}
        onClick={() => setOpen((value) => !value)}
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      </button>
      {open ? (
        <div className="crm-theme-panel crm-surface absolute z-30 w-[16.5rem] rounded-xl border border-slate-200 p-3.5 shadow-lg" role="dialog" aria-label="Thème">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Thème</p>
          <p className="mb-1.5 text-[11px] font-medium text-slate-600">Apparence</p>
          <div className="mb-3 grid grid-cols-3 gap-1 rounded-lg p-0.5" style={{ background: darkish ? "#1e293b" : "#f1f5f9" }}>
            <button type="button" className={theme === "light" ? "rounded-md bg-white px-1 py-1.5 text-xs font-medium text-slate-900 shadow-sm" : "rounded-md px-1 py-1.5 text-xs font-medium text-slate-400"} onClick={() => pickTheme("light")}>
              Clair
            </button>
            <button type="button" className={theme === "dark" ? "rounded-md bg-slate-700 px-1 py-1.5 text-xs font-medium text-slate-100 shadow-sm" : "rounded-md px-1 py-1.5 text-xs font-medium text-slate-600"} onClick={() => pickTheme("dark")}>
              Sombre
            </button>
            <button type="button" className={theme === "plus" ? "crm-theme-plus rounded-md px-1 py-1.5 text-xs font-medium text-white" : "rounded-md px-1 py-1.5 text-xs font-medium text-slate-400"} onClick={() => pickTheme("plus")}>
              Aurélia+
            </button>
          </div>
          {theme === "plus" ? (
            <p className="text-center text-[11px] font-medium text-slate-400">Bleu néon → violet électrique</p>
          ) : (
            <>
          <p className="mb-2 text-[11px] font-medium text-slate-600">Couleur d’accent</p>
          <div className="grid grid-cols-4 justify-items-center gap-2" role="listbox" aria-label="Couleur d’accent">
            {ACCENT_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={accent === id}
                title={ACCENTS[id].label}
                className={accent === id ? "h-7 w-7 rounded-full border-2 border-slate-800 ring-2 ring-slate-400/40" : "h-7 w-7 rounded-full border-2 border-white shadow-sm"}
                style={{ background: ACCENTS[id].swatch }}
                onClick={() => setAccent(id)}
              />
            ))}
          </div>
          <p className="mt-2 text-center text-[10px] text-slate-500">{current.label}</p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
